import { useCallback, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  setShowModal,
  setPrintPermission,
  setPrintPayload,
  setLoading,
  setError,
  resetQzTrayPrintState,
} from "../../store/slices/qztray/qzTrayPrintSlice";

import QzTrayPayloadService from "../../services/qztray/QzTrayPayloadService";

import { RootState } from "../../store/store";

import { TicketPrintPermissionDTO } from "../../types/qztray/QzTrayTypes";

import useApiErrorHandler from "../useApiErrorHandler";

import useLoading from "../useLoading";

import useAlerts from "../useAlerts";

/**
 * 🖨️ Hook: useQzTrayPrint
 *
 * Controla el flujo completo de impresión QZ Tray:
 * - Guarda permiso
 * - Evita duplicados
 * - Obtiene payload
 * - Abre modal automáticamente
 */
export const useQzTrayPrint = () => {

  const dispatch = useDispatch();

  const {
    showModal,
    permission,
    payload,
    isLoading,
    error
  } = useSelector(
    (state: RootState) => state.qzTrayPrint
  );

  const { handle } = useApiErrorHandler();

  const {
    showLoadingIndicator,
    hideLoadingIndicator
  } = useLoading();

  const { show: showAlert } = useAlerts();

  // ========================================================
  // 🚫 DUPLICATE GUARD
  // ========================================================

  const processedRef = useRef<Set<string>>(new Set());

  // ========================================================
  // 🖨️ FETCH PAYLOAD
  // ========================================================

  const fetchPayload = useCallback(

    async (type: string, id: number) => {

      try {

        dispatch(setLoading(true));

        showLoadingIndicator();

        const response =
          await QzTrayPayloadService.getPayload(
            type,
            id
          );

        // console.log(
        //   "🖨️ Payload response:",
        //   response
        // );

        const {
          statusCode,
          data
        } = response;

        if (statusCode === 200 && data) {

          dispatch(
            setPrintPayload(data)
          );

          dispatch(
            setShowModal(true)
          );

        } else {

          showAlert(
            "error",
            "Error",
            "No se pudo generar el payload."
          );
        }

      } catch (error: any) {

        dispatch(
          setError(
            error?.message ||
            "Error al obtener el payload."
          )
        );

        handle(error);

      } finally {

        dispatch(setLoading(false));

        hideLoadingIndicator();
      }
    },

    [
      dispatch,
      handle,
      showAlert,
      showLoadingIndicator,
      hideLoadingIndicator
    ]
  );

  // ========================================================
  // 🧾 SET PERMISSION
  // ========================================================

  const setPermission = useCallback(

    (data: TicketPrintPermissionDTO) => {

      // ================================================
      // 🚫 INVALID
      // ================================================

      if (
        !data.allowPrint ||
        !data.id ||
        !data.type
      ) {
        return;
      }

      // ================================================
      // 🚫 DUPLICATE
      // ================================================

      const key =
        `${data.type}-${data.id}`;

      if (
        processedRef.current.has(key)
      ) {

        console.warn(
          `🚫 Print duplicado evitado: ${key}`
        );

        return;
      }

      processedRef.current.add(key);

      // ================================================
      // ✅ STORE
      // ================================================

      dispatch(
        setPrintPermission(data)
      );

      // ================================================
      // ✅ FETCH
      // ================================================

      fetchPayload(
        data.type,
        data.id
      );

      // ================================================
      // 🧹 AUTO CLEAN
      // ================================================

      setTimeout(() => {

        processedRef.current.delete(key);

      }, 10000);
    },

    [dispatch, fetchPayload]
  );

  // ========================================================
  // 🎛️ MODAL CONTROL
  // ========================================================

  const openPrintModal = useCallback(() => {

    dispatch(setShowModal(true));

  }, [dispatch]);

  const closePrintModal = useCallback(() => {

    dispatch(setShowModal(false));

  }, [dispatch]);

  // ========================================================
  // 🧹 RESET
  // ========================================================

  const resetPrintState = useCallback(() => {

    processedRef.current.clear();

    dispatch(
      resetQzTrayPrintState()
    );

  }, [dispatch]);

  // ========================================================
  // 🧩 RETURN
  // ========================================================

  return {

    // state
    showModal,
    permission,
    payload,
    isLoading,
    error,

    // actions
    setPermission,
    fetchPayload,
    openPrintModal,
    closePrintModal,
    resetPrintState,
  };
};